import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, or } from 'ember-truth-helpers';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import cssVars from 'volta/modifiers/css-vars';

import type { TOC } from '@ember/component/template-only';
import type { TColorIcon, THueVariationString, TSize, TTextColor } from 'volta/utils/style-utils';

export interface IBvIconArgs {
  color?: THueVariationString | TColorIcon | TTextColor;
  glyph?: string;
  hasBackdrop?: boolean;
  label?: string;
  onClick?: (e: MouseEvent) => void;
  size?: TSize;
  tooltip?: string;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  class?: string;
}

export interface BvIconSignature {
  Args: IBvIconArgs;
  Blocks: {
    default: [];
  };
  Element: HTMLSpanElement;
}

const BvIconComponent: TOC<BvIconSignature> = <template>
  {{#let (if @color (concat '--bv-color-' @color)) as |colorVar|}}
    {{! template-lint-disable no-positive-tabindex }}
    <span
      class={{classNames
        'bv-icon'
        (concat 'bv-icon--' (orElse @size 'md'))
        (if @hasBackdrop 'bv-icon--has-backdrop')
        (if @onClick 'u-cursor-pointer')
        @class
      }}
      aria-label={{or @label @glyph}}
      tabindex={{if (or @tooltip @onClick) null '-1'}}
      ...attributes
      {{on 'click' (fn (optional @onClick))}}
      {{cssVars
        bv-icon-color=(if colorVar (concat 'var(' colorVar ')'))
        bv-icon-backdrop-color=(if
          (and colorVar @hasBackdrop) (concat 'rgba(var(' colorVar '-rgb), 0.15)')
        )
      }}
    >
      {{#if @glyph}}
        {{svgJar @glyph role='presentation' aria-hidden=(if @onClick 'true' 'false')}}
      {{/if}}
      {{#if @tooltip}}
        <BvTooltip @text={{@tooltip}} @side={{@tooltipSide}} />
      {{/if}}
      {{yield}}
    </span>
  {{/let}}
</template>;

export default BvIconComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvIcon: typeof BvIconComponent;
    'bv-icon': typeof BvIconComponent;
  }
}
